import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import {
  getTournamentBySlug,
  loadTournamentData,
  type TournamentConfig,
  type TournamentData,
} from "@/data/tournaments";

interface TournamentContextValue {
  /** Lightweight config (always available after slug resolves) */
  config: TournamentConfig | null;
  /** Full data including teams, matches — loaded lazily */
  data: TournamentData | null;
  /** Current tournament slug from URL */
  slug: string | null;
  /** Loading state for full data */
  loading: boolean;
  /** Error if tournament not found */
  error: string | null;
}

const TournamentContext = createContext<TournamentContextValue>({
  config: null,
  data: null,
  slug: null,
  loading: true,
  error: null,
});

export function TournamentProvider({
  children,
  tournamentSlug,
}: {
  children: ReactNode;
  tournamentSlug?: string;
}) {
  const params = useParams<{ slug: string }>();
  const slug = tournamentSlug || params.slug || null;

  const [config, setConfig] = useState<TournamentConfig | null>(null);
  const [data, setData] = useState<TournamentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setConfig(null);
      setData(null);
      setLoading(false);
      setError("No tournament selected");
      return;
    }

    // Instantly set config from registry
    const cfg = getTournamentBySlug(slug);
    if (!cfg) {
      setConfig(null);
      setData(null);
      setLoading(false);
      setError(`Tournament "${slug}" not found`);
      return;
    }

    setConfig(cfg);
    setError(null);
    setLoading(true);

    // Lazy-load full tournament data
    loadTournamentData(slug).then((d) => {
      if (d) {
        setData(d);
      } else {
        setError(`Failed to load data for "${slug}"`);
      }
      setLoading(false);
    });
  }, [slug]);

  return (
    <TournamentContext.Provider value={{ config, data, slug, loading, error }}>
      {children}
    </TournamentContext.Provider>
  );
}

/** Use tournament context — must be inside TournamentProvider */
export function useTournament() {
  const ctx = useContext(TournamentContext);
  return ctx;
}

/** Convenience: get just the config (throws if not in context) */
export function useTournamentConfig(): TournamentConfig {
  const { config } = useTournament();
  if (!config) throw new Error("useTournamentConfig must be used inside a TournamentProvider with a valid tournament");
  return config;
}
